"use client"

import { css, keyframes } from "@emotion/react"
import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"

type Props = {
  data: any
}

// --- Per-count mosaic layouts (hero = first image, on the RTL reading start / right).
// grid-template-areas: the first column token maps to inline-start (right in RTL). ---

type Layout = { aspect: string; cols: string; rows: string; areas: string; tiles: string[] }

const LAYOUTS: Record<number, Layout> = {
  1: { aspect: "2 / 1", cols: "1fr", rows: "1fr", areas: `"a"`, tiles: ["a"] },
  2: { aspect: "2 / 1", cols: "1fr 1fr", rows: "1fr", areas: `"a b"`, tiles: ["a", "b"] },
  3: {
    aspect: "2 / 1",
    cols: "2fr 1fr 1fr",
    rows: "1fr",
    areas: `"hero a b"`,
    tiles: ["hero", "a", "b"],
  },
  4: {
    aspect: "3 / 2",
    cols: "1fr 1fr",
    rows: "1fr 1fr",
    areas: `"a b" "c d"`,
    tiles: ["a", "b", "c", "d"],
  },
  5: {
    aspect: "2 / 1",
    cols: "2fr 1fr 1fr",
    rows: "1fr 1fr",
    areas: `"hero a b" "hero c d"`,
    tiles: ["hero", "a", "b", "c", "d"],
  },
  6: {
    aspect: "2 / 1",
    cols: "2fr 1fr 1fr 1fr 1fr 1fr 1fr",
    rows: "1fr 1fr",
    areas: `"hero t1 t1 t1 t2 t2 t2" "hero b1 b1 b2 b2 b3 b3"`,
    tiles: ["hero", "t1", "t2", "b1", "b2", "b3"],
  },
}

const layoutFor = (count: number): Layout => LAYOUTS[Math.min(Math.max(count, 1), 6)]

// --- Staggered fade-in (ASH-2040) ---

/* Tiles fade up in the order 3, 2, 6, 5, 4, 1 rather than left to right — the
   3rd tile leads and the hero lands last. Positions past a category's image
   count drop out, so a 4-image category runs 3, 2, 4, 1. */
const FADE_ORDER = [3, 2, 6, 5, 4, 1]
const FADE_STEP_MS = 120

function fadeDelay(index: number, count: number): number {
  const rank = FADE_ORDER.filter((position) => position <= count).indexOf(index + 1)
  return (rank === -1 ? index : rank) * FADE_STEP_MS
}

const fadeIn = keyframes`
  from { opacity: 0 }
  to   { opacity: 1 }
`

/* Held at 0 until the mosaic is both mounted and on screen; the animation
   (not a transition) is what runs, so a remount replays it from the start.
   `fadeIn` must be interpolated as the object, not a string — that is what
   makes Emotion emit the @keyframes rule alongside the animation name. */
const fadeInTile = ({ $animate, $delay }: { $animate: boolean; $delay: number }) => css`
  opacity: 0;
  ${$animate &&
  css`
    animation: ${fadeIn} 0.6s ease ${$delay}ms forwards;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`

// --- Styled components ---

const Section = styled.section`
  /* 24px gutters give the 327px content width the mobile Figma frame is built
     on at 375px, which the stack tiles below size themselves from (ASH-2053). */
  padding: 48px 24px;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 120px;
  }
`

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 64px;
  }
`

const Header = styled.div<{ $sideBySide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 840px;
    margin: 0 auto;
  }

  /* variant B keeps the header centered but full-width (per Figma 4793:21686);
     the side-by-side pane is what changes, not the header alignment (ASH-1668) */
  ${(props) => props.theme.breakpoints.up("lg")} {
    ${({ $sideBySide }) => $sideBySide && "max-width: none;"}
  }
`

/* Rich-text title: bold word renders underlined in the heading colour */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--comp-heading);
  margin: 0;
  letter-spacing: -0.8px;

  p {
    margin: 0;
    display: inline;
  }
  strong {
    font-weight: 700;
    text-decoration: none;
    display: inline-block;
    position: relative;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Subtitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    line-height: 1.3;
  }
`

const RunningText = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--comp-text);
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    line-height: 1.3;
  }
`

// --- Tabs (desktop) ---

const TabBar = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: center;
    border-bottom: 1px solid color-mix(in srgb, var(--comp-text) 15%, transparent);
  }
`

/* Each tab label is a real heading so both category names are in the markup as
   h3, not just the selected one (ASH-2191). role="presentation" keeps the
   tablist owning its tabs directly, per the WAI-ARIA tabs pattern. */
const TabHeading = styled.h3`
  flex: 1;
  min-width: 0;
  margin: 0;
  display: flex;
`

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 4px 16px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? "var(--comp-heading)" : "transparent")};
  margin-bottom: -1px;
  font-size: 24px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  color: ${({ $active }) => ($active ? "var(--comp-heading)" : "var(--comp-text)")};
  cursor: pointer;
  text-align: center;

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Mosaic (desktop) ---

const Mosaic = styled.div<{ $cfg: Layout }>`
  display: grid;
  gap: 8px;
  width: 100%;
  aspect-ratio: ${({ $cfg }) => $cfg.aspect};
  grid-template-columns: ${({ $cfg }) => $cfg.cols};
  grid-template-rows: ${({ $cfg }) => $cfg.rows};
  grid-template-areas: ${({ $cfg }) => $cfg.areas};
`

const Tile = styled.button<{ $area: string; $animate: boolean; $delay: number }>`
  grid-area: ${({ $area }) => $area};
  position: relative;
  overflow: hidden;
  border: none;
  padding: 0;
  cursor: pointer;
  background: transparent;
  ${fadeInTile}
`

const TileMedia = styled(Image)`
  object-fit: cover;
`

const TileVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/* "+X" overlay on the last visible tile when a category has more than 6 images */
const MoreOverlay = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 40px;
  font-weight: 700;
  text-decoration: none;
  border: none;
  cursor: pointer;
  width: 100%;

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: -2px;
  }
`

// --- Types ---

type MediaItem = { url: string; alt: string; isVideo: boolean }
type LinkItem = { name: string; url: string; target?: string } | null
type Category = { name: string; images: MediaItem[]; text: string; cta: LinkItem }

// --- Lightbox (ASH-2033) ---

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(2, 10, 13, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

/* Anchored to the top of the image column, not the overlay, so it sits just
   above the slider instead of floating near the viewport top (ASH-2138). The
   inline-end offsets match LightboxImage's margins so it lines up with the
   image edge. */
const LightboxClose = styled.button`
  position: absolute;
  bottom: 100%;
  margin-bottom: 10px;
  inset-inline-end: 0;
  z-index: 10;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    inset-inline-end: 120px;
    width: 40px;
    height: 40px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    inset-inline-end: 240px;
  }
`

/* Positioning context for LightboxClose; its top edge is the image's top edge. */
const LightboxContent = styled.div`
  position: relative;
  width: 100%;
`

/* The height has to yield to the viewport. A fixed height plus the controls
   below and the close button above overflowed the centered overlay on short
   viewports, and because the overlay centers its content the overflow split
   evenly and clipped the slider at both ends (ASH-2138). The subtracted amount
   is the chrome: close button and its gap above, controls and their gap below,
   plus breathing room. */
const LightboxImage = styled.div`
  position: relative;
  height: min(280px, calc(100dvh - 160px));
  overflow: hidden;
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: min(492px, calc(100dvh - 200px));
    margin: 0 120px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    margin: 0 240px;
  }

  .swiper {
    padding-block: 0;
  }

  .swiper,
  .swiper-slide {
    height: 100%;
  }

  div.swiper-slide {
    opacity: 0 !important;
  }

  div.swiper-slide.swiper-slide-active,
  div.swiper-slide.swiper-slide-duplicate-active {
    opacity: 1 !important;
  }
`

const LightboxControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`

const LightboxArrowBtn = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
  color: #fff;

  svg {
    color: #fff;
    path {
      fill: #fff;
    }
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

const LightboxDot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 100px;
  border: ${({ $active }) => ($active ? "none" : "1px solid #fff")};
  background-color: ${({ $active }) => ($active ? "#fff" : "transparent")};
  padding: 0;
  cursor: pointer;

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function Lightbox({
  images,
  isRtl,
  onClose,
  startIndex = 0,
}: {
  images: MediaItem[]
  isRtl: boolean
  onClose: () => void
  startIndex?: number
}) {
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(startIndex)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft") swiperRef?.slidePrev()
      if (e.key === "ArrowRight") swiperRef?.slideNext()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [swiperRef, onClose])

  // fade+loop pointer-events fix
  useEffect(() => {
    if (!containerRef.current || images.length <= 1) return
    const slideEls = containerRef.current.querySelectorAll(".swiper-slide")
    slideEls.forEach((slide) => {
      const el = slide as HTMLElement
      const isActive =
        el.classList.contains("swiper-slide-active") ||
        el.classList.contains("swiper-slide-duplicate-active")
      el.style.pointerEvents = isActive ? "auto" : "none"
      el.style.zIndex = isActive ? "2" : "0"
    })
  }, [activeIndex, images.length])

  return (
    <LightboxOverlay
      role="dialog"
      aria-modal="true"
      aria-label={isRtl ? "גלריה" : "Gallery"}
      onClick={onClose}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
      <LightboxContent onClick={(e) => e.stopPropagation()}>
        <LightboxClose onClick={onClose} aria-label={isRtl ? "סגור" : "Close"}>
          <CloseIcon />
        </LightboxClose>
        <LightboxImage ref={containerRef}>
          <Swiper
            modules={[EffectFade]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            onSwiper={setSwiperRef}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            slidesPerView={1}
            initialSlide={startIndex}
            loop={images.length > 1}
            style={{ width: "100%", height: "100%" }}
          >
            {images.map((item, i) => (
              <SwiperSlide key={i}>
                {item.isVideo ? (
                  <TileVideo src={getUmbracoMediaUrl(item.url)} autoPlay muted loop playsInline />
                ) : (
                  <TileMedia
                    src={getUmbracoMediaUrl(item.url)}
                    fill
                    alt={item.alt}
                    sizes="(max-width: 768px) 100vw, 1200px"
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </LightboxImage>

        {images.length > 1 && (
          <LightboxControls>
            <LightboxArrowBtn
              onClick={() => swiperRef?.slidePrev()}
              aria-label={isRtl ? "הבא" : "Previous"}
            >
              {isRtl ? <RightArrow fill="currentColor" /> : <LeftArrow fill="currentColor" />}
            </LightboxArrowBtn>
            {images.map((_, i) => (
              <LightboxDot
                key={i}
                $active={i === activeIndex}
                onClick={() => swiperRef?.slideToLoop(i)}
                aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
              />
            ))}
            <LightboxArrowBtn
              onClick={() => swiperRef?.slideNext()}
              aria-label={isRtl ? "הקודם" : "Next"}
            >
              {isRtl ? <LeftArrow fill="currentColor" /> : <RightArrow fill="currentColor" />}
            </LightboxArrowBtn>
          </LightboxControls>
        )}
      </LightboxContent>
    </LightboxOverlay>
  )
}

// --- Per-category content below the grid ---

const PaneContent = styled.div<{ $sideBySide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: start;

  ${(props) => props.theme.breakpoints.up("lg")} {
    /* variant B: content shares the row with the mosaic as roughly equal halves
       (per Figma 4793:21686), not a narrow fixed column (ASH-1668) */
    ${({ $sideBySide }) => $sideBySide && "flex: 45 1 0; min-width: 0;"}
  }
`

/* wraps the mosaic so it shares the row with the content in variant B (lg up).
   54/45 split matches the Figma image sizes as closely as possible (ASH-2117). */
const MosaicWrap = styled.div<{ $sideBySide?: boolean }>`
  width: 100%;
  min-width: 0;

  ${(props) => props.theme.breakpoints.up("lg")} {
    flex: ${({ $sideBySide }) => ($sideBySide ? "54 1 0" : "1 1 0")};
  }
`

const CategoryTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
  }
`

const CategoryText = styled.div<{ $twoCol?: boolean }>`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--comp-text);

  * {
    &:first-of-type {
      margin-top: 0;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
  }

  a {
    color: var(--comp-heading);
    text-decoration: underline;
  }
  ul,
  ol {
    margin: 0;
    padding-inline-start: 20px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    ${({ $twoCol }) => $twoCol && "columns: 2; column-gap: 32px;"}
  }
`

const Actions = styled.div<{ $sideBySide?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    ${({ $sideBySide }) =>
      !$sideBySide &&
      `width: 50%;
       margin-inline-start: auto;`}
  }
`

const LinkButton = styled(Link)<{ $filled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid var(--comp-text);
  background: ${({ $filled }) => ($filled ? "var(--comp-heading)" : "transparent")};
  color: ${({ $filled }) => ($filled ? "var(--comp-bg)" : "var(--comp-text)")};

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 48px;
  }
`

// --- Desktop pane (tabs) wrapper ---

const DesktopView = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
`

const Pane = styled.div<{ $sideBySide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 32px;

  /* variant B: content column beside the mosaic instead of stacked below, only
     from lg up so the mosaic keeps enough room on tablet (ASH-1668) */
  ${(props) => props.theme.breakpoints.up("lg")} {
    ${({ $sideBySide }) =>
      $sideBySide &&
      `flex-direction: row;
       align-items: center;
       gap: 48px;`}
  }
`

// --- Mobile accordion ---

const MobileView = styled.div`
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const AccItem = styled.div`
  border-bottom: 1px solid color-mix(in srgb, var(--comp-text) 100%, transparent);
`

/* The mobile accordion header is the visible category heading, so it carries
   the h3 too (ASH-2191). Plain wrapper per the ARIA accordion pattern - no
   role="presentation" here, an accordion has no required-children constraint. */
const AccHeading = styled.h3`
  margin: 0;
`

const AccHeader = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: start;
  font-size: ${({ $open }) => ($open ? "24px" : "18px")};
  font-weight: 700;
  color: var(--comp-heading);
  transition: font-size 0.3s ease;
  padding-inline-start: ${({ $open }) => ($open ? "0" : "32px")};

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 20px 0;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const AccBody = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s ease;
`

const AccInner = styled.div`
  overflow: hidden;
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 8px;
  }
`

/* Figma mobile stack gives each position its own height at the 327px content
   width: 683.624 / 216.763 / 216.763 / 327 / 327 / 327 (ASH-2053). Expressed as
   ratios so the tiles keep the proportions on any viewport, not just 375px. */
const MOBILE_TILE_ASPECTS = [
  "327 / 683.624",
  "327 / 216.763",
  "327 / 216.763",
  "327 / 327",
  "327 / 327",
  "327 / 327",
]

const StackTile = styled.button<{ $aspect: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspect }) => $aspect};
  overflow: hidden;
  border: none;
  padding: 0;
  cursor: pointer;
  background: transparent;
`

const ShowMoreBtn = styled.button`
  align-self: center;
  height: 40px;
  padding: 0 24px;
  border-radius: 8px;
  border: 1px solid var(--comp-text);
  background: transparent;
  color: var(--comp-text);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  margin-top: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: unset;
  }
`

// --- Types & parsing ---

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

// CMS boolean/toggle → true. Umbraco returns "1"/"true" (or a real boolean).
function isToggleOn(v: unknown): boolean {
  return v === true || String(v).toLowerCase() === "true" || String(v) === "1"
}

function parseLink(raw: any): LinkItem {
  const link = Array.isArray(raw) ? raw[0] : undefined
  if (!link?.name) return null
  return {
    name: link.name,
    url: getLinkHrefFromMultiUrlPickerItem(link) || "#",
    target: link.target,
  }
}

function parseCategory(block: any): Category {
  const media = (extractProperty(block, "images", true) as any[] | undefined) || []
  return {
    name: (extractProperty(block, "categoryName") as string) || "",
    images: media
      .filter((m) => m?.url)
      .map((m) => ({
        url: m.url as string,
        alt: (m.altText as string) || "",
        isVideo: isVideoUrl(m.url),
      })),
    text: (extractProperty(block, "categoryText") as string) || "",
    cta: parseLink(extractProperty(block, "ctaButton")),
  }
}

// --- Media tile ---

function Media({ item, sizes }: { item: MediaItem; sizes: string }) {
  if (item.isVideo) {
    return <TileVideo src={getUmbracoMediaUrl(item.url)} autoPlay muted loop playsInline />
  }
  return <TileMedia src={getUmbracoMediaUrl(item.url)} fill alt={item.alt} sizes={sizes} />
}

// --- Variant B mosaic for 5+ images (ASH-2117) ---

const MosaicBWrap = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  aspect-ratio: 630 / 390;
`

const MosaicBMainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 0 0 62%;
  min-width: 0;
`

const MosaicBHero = styled.button<{ $animate: boolean; $delay: number }>`
  position: relative;
  flex: 3.5;
  overflow: hidden;
  border: none;
  padding: 0;
  cursor: pointer;
  background: transparent;
  ${fadeInTile}
`

const MosaicBBottomRow = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
`

const MosaicBCell = styled.button<{ $animate: boolean; $delay: number }>`
  position: relative;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border: none;
  padding: 0;
  cursor: pointer;
  background: transparent;
  ${fadeInTile}
`

// --- Desktop mosaic ---

function MosaicGrid({
  images,
  onMoreClick,
  onImageClick,
}: {
  images: MediaItem[]
  onMoreClick?: () => void
  // eslint-disable-next-line no-unused-vars
  onImageClick?: (index: number) => void
}) {
  const cfg = layoutFor(images.length)
  const visible = images.slice(0, cfg.tiles.length)
  const extra = images.length - cfg.tiles.length
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 })

  return (
    <Mosaic ref={ref} $cfg={cfg}>
      {visible.map((item, i) => {
        const isLast = i === cfg.tiles.length - 1
        const showOverlay = isLast && extra > 0
        return (
          <Tile
            key={i}
            $area={cfg.tiles[i]}
            $animate={inView}
            $delay={fadeDelay(i, visible.length)}
            onClick={() => onImageClick?.(i)}
            aria-label={item.alt || `Image ${i + 1}`}
          >
            <Media item={item} sizes="(max-width: 768px) 100vw, 600px" />
            {showOverlay && (
              <MoreOverlay
                onClick={(e) => {
                  e.stopPropagation()
                  onMoreClick?.()
                }}
              >
                +{extra}
              </MoreOverlay>
            )}
          </Tile>
        )
      })}
    </Mosaic>
  )
}

function MosaicGridB({
  images,
  onMoreClick,
  onImageClick,
}: {
  images: MediaItem[]
  onMoreClick?: () => void
  // eslint-disable-next-line no-unused-vars
  onImageClick?: (index: number) => void
}) {
  // declared before the early return so the hook order stays stable
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 })

  if (images.length < 5) {
    return <MosaicGrid images={images} onMoreClick={onMoreClick} onImageClick={onImageClick} />
  }

  const visible = images.slice(0, 5)
  const extra = images.length - 5

  return (
    <MosaicBWrap ref={ref}>
      {/* RTL: first child = right. Tall image on the right */}
      <MosaicBCell
        $animate={inView}
        $delay={fadeDelay(4, visible.length)}
        onClick={() => onImageClick?.(4)}
        aria-label={visible[4].alt || "Image 5"}
      >
        <Media item={visible[4]} sizes="250px" />
      </MosaicBCell>
      {/* Second child = left in RTL. Main column with hero + 3 small */}
      <MosaicBMainCol>
        <MosaicBHero
          $animate={inView}
          $delay={fadeDelay(0, visible.length)}
          onClick={() => onImageClick?.(0)}
          aria-label={visible[0].alt || "Image 1"}
        >
          <Media item={visible[0]} sizes="400px" />
        </MosaicBHero>
        <MosaicBBottomRow>
          {visible.slice(1, 4).map((item, i) => {
            const isLast = i === 2
            const showOverlay = isLast && extra > 0
            return (
              <MosaicBCell
                key={i}
                $animate={inView}
                $delay={fadeDelay(i + 1, visible.length)}
                onClick={() => onImageClick?.(i + 1)}
                aria-label={item.alt || `Image ${i + 2}`}
              >
                <Media item={item} sizes="130px" />
                {showOverlay && (
                  <MoreOverlay
                    onClick={(e) => {
                      e.stopPropagation()
                      onMoreClick?.()
                    }}
                  >
                    +{extra}
                  </MoreOverlay>
                )}
              </MosaicBCell>
            )
          })}
        </MosaicBBottomRow>
      </MosaicBMainCol>
    </MosaicBWrap>
  )
}

// --- Mobile accordion category ---

/* Figma stacks six images on mobile before the "show more" button. This drives
   both how many tiles render and whether the button shows, since the button is
   gated on the leftover count — at 3 a category of 4 to 6 images was truncated
   and showed the button early (ASH-2055). */
const MOBILE_PREVIEW = 6

function MobileCategory({
  category,
  open,
  onToggle,
  onShowMore,
  onImageClick,
  isRtl,
}: {
  category: Category
  open: boolean
  onToggle: () => void
  onShowMore: () => void
  // eslint-disable-next-line no-unused-vars
  onImageClick: (index: number) => void
  isRtl: boolean
}) {
  const shown = category.images.slice(0, MOBILE_PREVIEW)
  const hiddenCount = category.images.length - shown.length

  return (
    <AccItem>
      <AccHeading>
        <AccHeader onClick={onToggle} aria-expanded={open} $open={open}>
          <span>{category.name}</span>
        </AccHeader>
      </AccHeading>
      <AccBody $open={open}>
        <AccInner>
          <Stack>
            {shown.map((item, i) => (
              <StackTile
                key={i}
                $aspect={MOBILE_TILE_ASPECTS[i] ?? "327 / 327"}
                onClick={() => onImageClick(i)}
                aria-label={item.alt || `Image ${i + 1}`}
              >
                <Media item={item} sizes="100vw" />
              </StackTile>
            ))}
            {hiddenCount > 0 && (
              <ShowMoreBtn onClick={onShowMore}>
                {isRtl ? `הצג עוד תמונות (${hiddenCount}+)` : `Show more (+${hiddenCount})`}
              </ShowMoreBtn>
            )}
            {category.text && (
              <CategoryText
                dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(category.text, true) }}
              />
            )}
            {category.cta && (
              <Actions>
                <LinkButton
                  href={category.cta.url}
                  onClick={(e) => handleAnchorScroll(e, category.cta?.url)}
                  {...(category.cta.target === "_blank"
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {category.cta.name}
                </LinkButton>
              </Actions>
            )}
          </Stack>
        </AccInner>
      </AccBody>
    </AccItem>
  )
}

// --- Main component ---

function Gallery2({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const [activeTab, setActiveTab] = useState(0)
  const [openAccordion, setOpenAccordion] = useState(0)
  const [lightboxStart, setLightboxStart] = useState<number | null>(null)

  const title = extractProperty(data, "title") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const runningText = extractProperty(data, "runningText") as string | ""
  const categoryBlocks = extractProperty(data, "categories") as any[] | ""
  const categories = (Array.isArray(categoryBlocks) ? categoryBlocks : [])
    .map(parseCategory)
    .filter((c) => c.name || c.images.length > 0)
  const viewGallery = parseLink(extractProperty(data, "viewGalleryButton"))
  // variant B (ASH-1668): content column beside the mosaic on desktop. Toggled
  // from Umbraco via the `sideBySide` boolean.
  const sideBySide = isToggleOn(extractProperty(data, "sideBySide"))

  if (!title && categories.length === 0) return null

  const active = categories[Math.min(activeTab, categories.length - 1)]

  // the per-category text + CTAs, shared by both layouts (below the mosaic in the
  // default variant, beside it in variant B)
  const paneContent = active && (active.name || active.text || active.cta || viewGallery) && (
    <PaneContent $sideBySide={sideBySide}>
      {active.name && <CategoryTitle>{active.name}</CategoryTitle>}
      {active.text && (
        <CategoryText
          $twoCol={!sideBySide}
          dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(active.text, true) }}
        />
      )}
      {(active.cta || viewGallery) && (
        <Actions $sideBySide={sideBySide}>
          {active.cta && (
            <LinkButton
              href={active.cta.url}
              onClick={(e) => handleAnchorScroll(e, active.cta?.url)}
              {...(active.cta.target === "_blank"
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              {active.cta.name}
            </LinkButton>
          )}
          {viewGallery && (
            <LinkButton
              $filled
              href={viewGallery.url}
              onClick={(e) => handleAnchorScroll(e, viewGallery.url)}
              {...(viewGallery.target === "_blank"
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              {viewGallery.name}
            </LinkButton>
          )}
        </Actions>
      )}
    </PaneContent>
  )

  return (
    <Section>
      <Inner>
        <Header $sideBySide={sideBySide}>
          {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {runningText && <RunningText>{runningText}</RunningText>}
        </Header>

        {categories.length > 0 && (
          <>
            {/* Desktop: tabs + active pane */}
            <DesktopView>
              {categories.length > 1 && (
                <TabBar role="tablist" aria-label={isRtl ? "קטגוריות גלריה" : "Gallery categories"}>
                  {categories.map((c, i) => (
                    <TabHeading key={i} role="presentation">
                      <Tab
                        role="tab"
                        id={`gallery2-tab-${i}`}
                        aria-selected={i === activeTab}
                        aria-controls={`gallery2-panel-${i}`}
                        tabIndex={i === activeTab ? 0 : -1}
                        $active={i === activeTab}
                        onClick={() => setActiveTab(i)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          const last = categories.length - 1
                          let next = -1
                          if (e.key === "ArrowRight")
                            next = isRtl ? (i === 0 ? last : i - 1) : i === last ? 0 : i + 1
                          if (e.key === "ArrowLeft")
                            next = isRtl ? (i === last ? 0 : i + 1) : i === 0 ? last : i - 1
                          if (e.key === "Home") next = 0
                          if (e.key === "End") next = last
                          if (next >= 0) {
                            e.preventDefault()
                            setActiveTab(next)
                            document.getElementById(`gallery2-tab-${next}`)?.focus()
                          }
                        }}
                      >
                        {c.name}
                      </Tab>
                    </TabHeading>
                  ))}
                </TabBar>
              )}
              {active && (
                <Pane
                  $sideBySide={sideBySide}
                  role="tabpanel"
                  id={`gallery2-panel-${activeTab}`}
                  aria-labelledby={`gallery2-tab-${activeTab}`}
                  tabIndex={0}
                >
                  {/* variant B places the content column first so it lands on the
                      reading start (right in RTL); the default stacks mosaic then
                      content. Mobile is unaffected (accordion below). */}
                  {sideBySide && paneContent}
                  {/* keyed on the tab so switching category remounts the tiles and
                      replays the staggered fade-in (ASH-2040) */}
                  <MosaicWrap $sideBySide={sideBySide}>
                    {sideBySide && active.images.length >= 5 ? (
                      <MosaicGridB
                        key={activeTab}
                        images={active.images}
                        onMoreClick={() => setLightboxStart(0)}
                        onImageClick={(i) => setLightboxStart(i)}
                      />
                    ) : (
                      <MosaicGrid
                        key={activeTab}
                        images={active.images}
                        onMoreClick={() => setLightboxStart(0)}
                        onImageClick={(i) => setLightboxStart(i)}
                      />
                    )}
                  </MosaicWrap>
                  {!sideBySide && paneContent}
                </Pane>
              )}
            </DesktopView>

            {/* Mobile: accordion */}
            <MobileView>
              {categories.map((c, i) => (
                <MobileCategory
                  key={i}
                  category={c}
                  open={openAccordion === i}
                  onToggle={() => setOpenAccordion((prev) => (prev === i ? -1 : i))}
                  onShowMore={() => {
                    setActiveTab(i)
                    setLightboxStart(0)
                  }}
                  onImageClick={(imgIndex) => {
                    setActiveTab(i)
                    setLightboxStart(imgIndex)
                  }}
                  isRtl={isRtl}
                />
              ))}
            </MobileView>
          </>
        )}
      </Inner>

      {lightboxStart !== null && active && (
        <Lightbox
          images={active.images}
          isRtl={isRtl}
          onClose={() => setLightboxStart(null)}
          startIndex={lightboxStart}
        />
      )}
    </Section>
  )
}

export default Gallery2
